import { SourceCode } from "@/components/CodeGen/SourceCode/SourceCode";
import { EndpointCombobox } from "@/components/CodeGen/components/endpoint-combobox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { HeadersTable } from "@/components/ui/headers-table";
import { HttpMethod } from "@/components/ui/http-method";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { StatusCode } from "@/components/ui/status-code";
import { Textarea } from "@/components/ui/textarea";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useEndpoints } from "@/lib/hooks/useEndpoints";
import { useFetchEndpointRequest } from "@/lib/hooks/useFetchEndpointRequest";
import { cn } from "@/lib/utils";
import { useRequestStore } from "@/state/stores";
import { Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Endpoint, FetchEndpointRequestData } from "../../../../schemas";

export function PreviewView() {
  const chatId = useActiveChatId();

  const { data: endpointsData, isLoading: endpointsLoading } =
    useEndpoints(chatId);
  const [activeSection, setActiveSection] = useState<"playground" | "docs">(
    "playground",
  );
  const [activeTab, setActiveTab] = useState<"params" | "headers" | "body">(
    "params",
  );
  const [activeContentTab, setActiveContentTab] = useState<
    "request" | "response"
  >("response");

  // Get endpoints from API
  const endpoints = useMemo(() => {
    return endpointsData?.data?.endpoints || [];
  }, [endpointsData]);

  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(
    null,
  );

  // Local state for new entry forms
  const [newParam, setNewParam] = useState({ name: "", value: "" });
  const [newHeader, setNewHeader] = useState({ name: "", value: "" });

  const {
    params,
    headers,
    body,
    updateParam,
    updateHeader,
    setBody,
    addParam,
    addHeader,
    removeParam,
    removeHeader,
    populateFromEndpoint,
  } = useRequestStore();

  // Update selected endpoint when endpoints change
  useEffect(() => {
    if (endpoints.length > 0) {
      if (
        !selectedEndpoint ||
        !endpoints.find((e) => e.value === selectedEndpoint.value)
      ) {
        setSelectedEndpoint(endpoints[0]);
      }
    } else {
      setSelectedEndpoint(null);
    }
  }, [endpoints, selectedEndpoint]);

  // Auto-populate request structure when endpoint changes
  useEffect(() => {
    if (selectedEndpoint) {
      populateFromEndpoint({
        pathParams: selectedEndpoint.pathParams || [],
        queryParams: selectedEndpoint.queryParams || [],
        requiredHeaders: selectedEndpoint.requiredHeaders || [],
        bodySchema: selectedEndpoint.bodySchema,
        method: selectedEndpoint.method,
      });
    }
  }, [selectedEndpoint, populateFromEndpoint]);
  const fetchEndpointRequest = useFetchEndpointRequest();

  // Determine deployment and OpenAPI status
  const deploymentStatus = useMemo(() => {
    if (!chatId) {
      return "no-chat";
    }
    if (!endpointsData?.data) {
      return "loading";
    }

    const { endpoints, error } = endpointsData.data;

    if (endpoints && endpoints.length > 0) {
      return "has-endpoints";
    }

    if (error?.code === "OPENAPI_NOT_FOUND") {
      return "deployed-no-openapi";
    }

    // Explicitly not deployed or network errors mean app not deployed
    return "not-deployed";
  }, [endpointsData, chatId]);

  const hasDeployedApp = deploymentStatus === "has-endpoints";

  // Helper functions for new entry submission
  const handleNewParamSubmit = useCallback(() => {
    if (newParam.name.trim() && newParam.value.trim()) {
      const existingParam = params.find((p) => p.name === newParam.name.trim());
      if (!existingParam) {
        addParam({
          enabled: true,
          name: newParam.name.trim(),
          value: newParam.value.trim(),
        });
        setNewParam({ name: "", value: "" });
      }
    }
  }, [newParam, addParam, params]);

  const handleNewHeaderSubmit = useCallback(() => {
    if (newHeader.name.trim() && newHeader.value.trim()) {
      const existingHeader = headers.find(
        (h) => h.name === newHeader.name.trim(),
      );
      if (!existingHeader) {
        addHeader({
          enabled: true,
          name: newHeader.name.trim(),
          value: newHeader.value.trim(),
        });
        setNewHeader({ name: "", value: "" });
      }
    }
  }, [newHeader, addHeader, headers]);

  const makeFetchEndpointRequest = useCallback(
    (endpoint: Endpoint) => {
      const currentParams = useRequestStore.getState().params;
      const currentHeaders = useRequestStore.getState().headers;
      const currentBody = useRequestStore.getState().body;

      const enabledParams = currentParams.filter((p) => p.enabled);
      const enabledHeaders = currentHeaders.filter((h) => h.enabled);

      if (chatId && hasDeployedApp) {
        // Use fetch endpoint for deployed apps
        fetchEndpointRequest.mutate({
          chatId,
          request: {
            method: endpoint.method,
            path: endpoint.path,
            params: enabledParams.map((p) => ({
              name: p.name,
              value: p.value,
            })),
            headers: enabledHeaders.map((h) => ({
              name: h.name,
              value: h.value,
            })),
            body:
              endpoint.method !== "GET" && endpoint.method !== "HEAD"
                ? currentBody
                : undefined,
          },
        });
      }
    },
    [chatId, hasDeployedApp, fetchEndpointRequest.mutate],
  );

  const formattedRequestBody = useMemo(() => {
    const sentRequest = fetchEndpointRequest.variables?.request;
    if (!sentRequest?.body) {
      return null;
    }

    try {
      return JSON.stringify(JSON.parse(sentRequest.body), null, 2);
    } catch {
      return sentRequest.body;
    }
  }, [fetchEndpointRequest.variables]);

  const formattedResponseBody = useMemo(() => {
    const data = fetchEndpointRequest.data;
    if (!data?.body) {
      return "";
    }

    try {
      return JSON.stringify(JSON.parse(data.body), null, 2);
    } catch {
      return data.body;
    }
  }, [fetchEndpointRequest.data]);

  const renderRequestContent = () => {
    const data = fetchEndpointRequest.data;
    const variables = fetchEndpointRequest.variables;

    if (!data && !variables) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">No request data available</div>
        </div>
      );
    }

    const sentRequest = variables?.request;
    type ParamOrHeader = NonNullable<
      FetchEndpointRequestData["params"]
    >[number];
    const sentParams = sentRequest?.params || [];
    const sentHeaders = sentRequest?.headers || [];

    return (
      <div className="flex flex-col gap-6 p-4">
        {/* Method and URL */}
        <div className="flex items-center gap-3">
          <HttpMethod
            method={data?.method || sentRequest?.method || ""}
            className="font-mono text-sm font-medium"
          />
          <span className="font-mono text-sm">
            {data?.url || sentRequest?.path}
          </span>
        </div>

        {/* Parameters */}
        {sentParams.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              Parameters
            </h4>
            <div className="border rounded-md overflow-hidden">
              <div className="grid grid-cols-2 gap-px bg-border">
                <div className="bg-background px-3 py-2 text-sm font-medium text-muted-foreground">
                  Name
                </div>
                <div className="bg-background px-3 py-2 text-sm font-medium text-muted-foreground">
                  Value
                </div>
              </div>
              {sentParams.map((param: ParamOrHeader) => (
                <div
                  key={`${param.name}-${param.value}`}
                  className="grid grid-cols-2 gap-px bg-border"
                >
                  <div className="bg-background px-3 py-2 text-sm font-mono">
                    {param.name}
                  </div>
                  <div className="bg-background px-3 py-2 text-sm font-mono">
                    {param.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Headers */}
        {sentHeaders.length > 0 && (
          <HeadersTable headers={sentHeaders} className="mx-4" />
        )}

        {/* Body */}
        {formattedRequestBody && (
          <SourceCode content={formattedRequestBody} isStreaming={false} />
        )}
      </div>
    );
  };

  const renderResponseContent = () => {
    const isLoading = fetchEndpointRequest.isPending;
    const error = fetchEndpointRequest.error;
    const data = fetchEndpointRequest.data;

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      );
    }

    if (error) {
      return (
        <SourceCode
          content={JSON.stringify({ error: error.message }, null, 2)}
          isStreaming={false}
        />
      );
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">No response data</div>
        </div>
      );
    }

    const responseHeaders = Object.entries(data.headers || {});

    return (
      <div className="flex flex-col gap-6 p-4">
        {/* Response Headers */}
        {responseHeaders.length > 0 && (
          <HeadersTable
            headers={responseHeaders.map(([name, value]) => ({
              name,
              value,
            }))}
            className="mx-4"
          />
        )}

        {/* Response Body */}
        <SourceCode content={formattedResponseBody} isStreaming={false} />
      </div>
    );
  };

  const renderParamsContent = () => {
    switch (activeTab) {
      case "params":
        return (
          <div>
            {params.map((param) => (
              <div key={param.id} className="flex items-center gap-4">
                <Checkbox
                  id={`param-${param.id}`}
                  checked={param.enabled}
                  onCheckedChange={(checked) =>
                    updateParam(param.id, { enabled: checked as boolean })
                  }
                />
                <Input
                  type="text"
                  placeholder="name"
                  value={param.name}
                  onChange={(e) =>
                    updateParam(param.id, { name: e.target.value })
                  }
                  className="bg-transparent border-0 text-sm text-muted-foreground h-8"
                />
                <Input
                  type="text"
                  placeholder="value"
                  value={param.value}
                  onChange={(e) =>
                    updateParam(param.id, { value: e.target.value })
                  }
                  className="bg-transparent border-0 text-sm text-muted-foreground h-8"
                />
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => removeParam(param.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-4">
              <Checkbox
                id="param-new"
                checked={false}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleNewParamSubmit();
                  }
                }}
              />
              <Input
                type="text"
                placeholder="name"
                value={newParam.name}
                onChange={(e) =>
                  setNewParam((prev) => ({ ...prev, name: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNewParamSubmit();
                  }
                }}
                className="bg-transparent border-0 text-sm text-muted-foreground h-8"
              />
              <Input
                type="text"
                placeholder="value"
                value={newParam.value}
                onChange={(e) =>
                  setNewParam((prev) => ({ ...prev, value: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNewParamSubmit();
                  }
                }}
                className="bg-transparent border-0 text-sm text-muted-foreground h-8"
              />
              <div className="w-6 h-6" />
            </div>
          </div>
        );

      case "headers":
        return (
          <div className="flex flex-col gap-3">
            {headers.map((header) => (
              <div key={header.id} className="flex items-center gap-4">
                <Checkbox
                  id={`header-${header.id}`}
                  checked={header.enabled}
                  onCheckedChange={(checked) =>
                    updateHeader(header.id, { enabled: checked as boolean })
                  }
                />
                <Input
                  type="text"
                  placeholder="name"
                  value={header.name}
                  onChange={(e) =>
                    updateHeader(header.id, { name: e.target.value })
                  }
                  className="bg-transparent border-0 text-sm text-muted-foreground h-8"
                />
                <Input
                  type="text"
                  placeholder="value"
                  value={header.value}
                  onChange={(e) =>
                    updateHeader(header.id, { value: e.target.value })
                  }
                  className="bg-transparent border-0 text-sm text-muted-foreground h-8"
                />
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => removeHeader(header.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-4">
              <Checkbox
                id="header-new"
                checked={false}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleNewHeaderSubmit();
                  }
                }}
              />
              <Input
                type="text"
                placeholder="name"
                value={newHeader.name}
                onChange={(e) =>
                  setNewHeader((prev) => ({ ...prev, name: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNewHeaderSubmit();
                  }
                }}
                className="bg-transparent border-0 text-sm text-muted-foreground h-8"
              />
              <Input
                type="text"
                placeholder="value"
                value={newHeader.value}
                onChange={(e) =>
                  setNewHeader((prev) => ({ ...prev, value: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNewHeaderSubmit();
                  }
                }}
                className="bg-transparent border-0 text-sm text-muted-foreground h-8"
              />
              <div className="w-6 h-6" />
            </div>
          </div>
        );

      case "body":
        return (
          <div className="flex flex-col gap-3">
            <Textarea
              placeholder="Enter request body (JSON)"
              className="min-h-[200px] font-mono text-sm"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (!hasDeployedApp) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-muted-foreground">
            {deploymentStatus === "deployed-no-openapi"
              ? "OpenAPI specification not found—ask the agent to add OpenAPI support to your app."
              : "No deploy yet—ask the agent to ship your app, and it'll pop up here."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full gap-3">
        <div className="border-b border-border">
          <div className="flex gap-4 py-2 px-6">
            {["playground", "docs"].map((tab) => (
              <Button
                key={tab}
                variant="ghost"
                className={cn(
                  "h-6 px-2 py-0 text-sm rounded gap-2 justify-center items-center",
                  activeSection === tab
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground",
                )}
                onClick={() => setActiveSection(tab as "playground" | "docs")}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {activeSection === "docs" ? (
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-6">
                {!hasDeployedApp ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="text-muted-foreground">
                        {deploymentStatus === "deployed-no-openapi"
                          ? "OpenAPI specification not found—ask the agent to add OpenAPI support to your app."
                          : "No deploy yet—ask the agent to ship your app, and documentation will appear here."}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-semibold mb-4">
                        API Endpoints
                      </h2>
                      <div className="text-sm text-muted-foreground mb-4">
                        {endpoints.length} endpoint
                        {endpoints.length !== 1 ? "s" : ""} available
                      </div>
                    </div>

                    <div className="space-y-4">
                      {endpoints.map((endpoint) => (
                        <div
                          key={endpoint.value}
                          className="border rounded-lg p-4 space-y-3"
                        >
                          <div className="flex items-center gap-3">
                            <HttpMethod method={endpoint.method} />
                            <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                              {endpoint.path}
                            </code>
                          </div>

                          {endpoint.pathParams &&
                            endpoint.pathParams.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium mb-2">
                                  Path Parameters
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {endpoint.pathParams.map((param) => (
                                    <code
                                      key={param}
                                      className="font-mono bg-muted font-thin text-primary-foreground/80 px-1 py-0.5 rounded"
                                    >
                                      {param}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            )}

                          {endpoint.queryParams &&
                            endpoint.queryParams.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium mb-2">
                                  Query Parameters
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {endpoint.queryParams.map((param) => (
                                    <code
                                      key={param}
                                      className="font-mono bg-muted font-thin text-primary-foreground/80 px-1 py-0.5 rounded"
                                    >
                                      {param}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            )}

                          {endpoint.requiredHeaders &&
                            endpoint.requiredHeaders.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium mb-2">
                                  Required Headers
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {endpoint.requiredHeaders.map((header) => (
                                    <code
                                      key={header}
                                      className="font-mono bg-muted font-thin text-primary-foreground/80 px-1 py-0.5 rounded"
                                    >
                                      {header}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            )}

                          {endpoint.bodySchema != null && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">
                                Request Body Schema
                              </h4>
                              <SourceCode
                                content={JSON.stringify(
                                  endpoint.bodySchema,
                                  null,
                                  2,
                                )}
                                isStreaming={false}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <>
            <div className="px-6 flex items-center gap-3">
              {selectedEndpoint && (
                <>
                  <EndpointCombobox
                    endpoints={endpoints}
                    value={selectedEndpoint}
                    onChange={setSelectedEndpoint}
                  />
                  <Button
                    variant="primary"
                    onClick={() => makeFetchEndpointRequest(selectedEndpoint)}
                    disabled={
                      fetchEndpointRequest.isPending ||
                      endpointsLoading ||
                      !hasDeployedApp
                    }
                    className="px-4 py-2 text-sm"
                  >
                    Run
                  </Button>
                </>
              )}
              {endpointsLoading && (
                <span className="text-sm text-muted-foreground">
                  Loading endpoints...
                </span>
              )}
              {!endpointsLoading && endpoints.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  No OpenAPI endpoints available—ask the agent to add OpenAPI
                  support
                </span>
              )}
            </div>

            <div className="border-y border-border">
              <div className="flex gap-4 py-2 px-6">
                {["params", "headers", "body"].map((tab) => (
                  <Button
                    key={tab}
                    variant="ghost"
                    className={cn(
                      "h-6 px-2 py-0 text-sm rounded gap-2 justify-center items-center",
                      activeTab === tab
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground",
                    )}
                    onClick={() =>
                      setActiveTab(tab as "params" | "headers" | "body")
                    }
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="px-6">{renderParamsContent()}</div>

            <div className="flex flex-col flex-1 min-h-0">
              <div className="border-y border-border">
                <div className="flex gap-4 py-2 px-6">
                  {["request", "response"].map((tab) => (
                    <Button
                      key={tab}
                      variant="ghost"
                      className={cn(
                        "h-6 px-2 py-0 text-sm rounded gap-2 justify-center items-center",
                        activeContentTab === tab
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground",
                      )}
                      onClick={() =>
                        setActiveContentTab(tab as "request" | "response")
                      }
                    >
                      {tab === "response" ? (
                        <>
                          Response
                          {fetchEndpointRequest.data && (
                            <StatusCode
                              statusCode={fetchEndpointRequest.data.status}
                            />
                          )}
                        </>
                      ) : (
                        tab.charAt(0).toUpperCase() + tab.slice(1)
                      )}
                    </Button>
                  ))}
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div key={activeContentTab}>
                  {activeContentTab === "request"
                    ? renderRequestContent()
                    : renderResponseContent()}
                </div>
              </ScrollArea>
            </div>
          </>
        )}
      </div>
    </>
  );
}
